// Amos-104 R1 — requester auth screens: forced password change on first sign-in, and forgot-password.
// Wording lives in COPY (provisional until Shelle signs off). Nothing here reveals whether an account exists.
import React, { useState } from 'react';
import { confirmResetPassword, confirmSignIn, resetPassword, updatePassword } from 'aws-amplify/auth';
import { COPY } from './copy';

const INPUT = 'w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
const BUTTON = 'w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors';
const LINK = 'text-sm text-blue-700 underline hover:text-blue-900';

/** The requester pool (us-east-1_lmixPBIT6) has case-SENSITIVE usernames (no UsernameConfiguration; changing that
 * needs a new pool) and every account is created lowercase, so a capitalised email silently fails sign-in.
 * Every auth call normalises the typed email the same way: trimmed, lowercased. */
export function normalizeUsername(email: string): string { return email.trim().toLowerCase(); }

function errName(err: unknown): string { return String((err as { name?: string })?.name || ''); }

/** Cognito password-policy failures, shown plainly: its own reason when it gives one, else the rules line. */
export function passwordPolicyMessage(err: unknown): string | null {
  const name = errName(err);
  const msg = String((err as { message?: string })?.message || '');
  if (name === 'InvalidPasswordException' || /password did not conform|password.*policy/i.test(msg)) {
    const reason = msg.replace(/^.*?policy:\s*/i, '').trim();
    if (!reason || reason === msg || /canopy/i.test(reason)) return COPY.signInPasswordWeak;
    return `${reason.charAt(0).toUpperCase()}${reason.slice(1)}${/[.!]$/.test(reason) ? '' : '.'}`;
  }
  return null;
}

/** Amos-104 S1: the pool's password policy (us-east-1_lmixPBIT6, read 2026-11-02): 12+ chars, upper, lower, number; symbols optional. */
export const PASSWORD_RULES: Array<{ key: string; label: string; ok: (pw: string) => boolean }> = [
  { key: 'length', label: COPY.signInRuleLength, ok: (pw) => pw.length >= 12 },
  { key: 'upper', label: COPY.signInRuleUpper, ok: (pw) => /[A-Z]/.test(pw) },
  { key: 'lower', label: COPY.signInRuleLower, ok: (pw) => /[a-z]/.test(pw) },
  { key: 'number', label: COPY.signInRuleNumber, ok: (pw) => /[0-9]/.test(pw) },
];
export const passwordMeetsRules = (pw: string) => PASSWORD_RULES.every((r) => r.ok(pw));

/** Inline checklist under a new-password field; each rule ticks as it is met. */
export function PasswordRules({ pw }: { pw: string }) {
  return (
    <div className="text-xs text-slate-500" data-testid="portal-pw-rules">
      <p>{COPY.signInPasswordRules}</p>
      <ul className="mt-1 space-y-0.5" aria-live="polite">
        {PASSWORD_RULES.map((r) => {
          const met = r.ok(pw);
          return (
            <li key={r.key} data-testid={`portal-pw-rule-${r.key}`} data-met={met ? 'yes' : 'no'} className={met ? 'text-green-700' : 'text-slate-500'}>
              <span aria-hidden="true">{met ? '\u2713' : '\u25CB'}</span> {r.label}<span className="sr-only">{met ? ' (met)' : ' (not yet met)'}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Shell({ testId, title, children }: { testId: string; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md" data-testid={testId}>
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-slate-800">{COPY.appTitle}</h1>
        <p className="text-sm text-slate-500 mt-1">{COPY.appSubtitle}</p>
        <h2 className="text-lg font-semibold text-slate-800 mt-6">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Alert({ text }: { text: string }) {
  return text ? <div role="alert" className="text-red-700 text-sm bg-red-50 border border-red-200 rounded-md p-3">{text}</div> : null;
}

function Field({ id, label, type = 'text', value, onChange, autoComplete }: { id: string; label: string; type?: string; value: string; onChange: (v: string) => void; autoComplete?: string }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      <input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} required autoComplete={autoComplete} className={INPUT} />
    </div>
  );
}

/** Shown when sign-in returns CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED (admin-created user, temporary password). */
export function NewPasswordScreen({ onDone, onExpired }: { onDone: () => void; onExpired: () => void }) {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (pw !== pw2) { setError(COPY.signInPasswordMismatch); return; }
    if (!passwordMeetsRules(pw)) { setError(COPY.signInPasswordWeak); return; }
    setBusy(true);
    try {
      const r = await confirmSignIn({ challengeResponse: pw });
      if (r.isSignedIn) onDone();
      else setError(COPY.signInFailed);
    } catch (err) {
      const policy = passwordPolicyMessage(err);
      if (policy) setError(policy);
      else if (errName(err) === 'InvalidParameterException') setError(COPY.signInPasswordWeak);
      else if (errName(err) === 'NotAuthorizedException' || errName(err) === 'ExpiredCodeException') onExpired();
      else { console.error('portal new-password error', err); setError(COPY.signInFailed); }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell testId="portal-new-password" title={COPY.signInNewTitle}>
      <p className="text-sm text-slate-600 mb-4">{COPY.signInNewIntro}</p>
      <form onSubmit={submit} className="space-y-4">
        <Field id="p-newpw" label={COPY.signInNewLabel} type="password" value={pw} onChange={setPw} autoComplete="new-password" />
        <Field id="p-newpw2" label={COPY.signInNewConfirmLabel} type="password" value={pw2} onChange={setPw2} autoComplete="new-password" />
        <PasswordRules pw={pw} />
        <Alert text={error} />
        <button type="submit" disabled={busy} className={BUTTON}>{busy ? COPY.signInNewBusy : COPY.signInNewButton}</button>
      </form>
    </Shell>
  );
}

/** Forgot password: email -> emailed code + new password. Same wording whether or not the account exists. */
export function ForgotPasswordScreen({ initialEmail, onBack, onDone }: { initialEmail: string; onBack: () => void; onDone: () => void }) {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const sendCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError('');
    setBusy(true);
    try {
      await resetPassword({ username: normalizeUsername(email) });
      setStep('code');
    } catch (err) {
      const n = errName(err);
      if (n === 'LimitExceededException' || n === 'TooManyRequestsException') setError(COPY.signInResetLimit);
      else if (n === 'UserNotFoundException' || n === 'InvalidParameterException' || n === 'NotAuthorizedException') setStep('code'); // same screen either way
      else { console.error('portal reset error', err); setError(COPY.signInResetFailed); }
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (pw !== pw2) { setError(COPY.signInPasswordMismatch); return; }
    if (!passwordMeetsRules(pw)) { setError(COPY.signInPasswordWeak); return; }
    setBusy(true);
    try {
      await confirmResetPassword({ username: normalizeUsername(email), confirmationCode: code.trim(), newPassword: pw });
      onDone();
    } catch (err) {
      const n = errName(err);
      const policy = passwordPolicyMessage(err);
      if (policy) setError(policy);
      else if (n === 'CodeMismatchException' || n === 'ExpiredCodeException' || n === 'UserNotFoundException' || n === 'NotAuthorizedException') setError(COPY.signInResetBadCode);
      else if (n === 'LimitExceededException' || n === 'TooManyRequestsException' || n === 'TooManyFailedAttemptsException') setError(COPY.signInResetLimit);
      else { console.error('portal confirm-reset error', err); setError(COPY.signInResetFailed); }
    } finally {
      setBusy(false);
    }
  };

  if (step === 'email') {
    return (
      <Shell testId="portal-forgot" title={COPY.signInForgotTitle}>
        <p className="text-sm text-slate-600 mb-4">{COPY.signInForgotIntro}</p>
        <form onSubmit={sendCode} className="space-y-4">
          <Field id="p-reset-email" label={COPY.signInEmail} type="email" value={email} onChange={setEmail} autoComplete="email" />
          <Alert text={error} />
          <button type="submit" disabled={busy} className={BUTTON}>{busy ? COPY.signInForgotBusy : COPY.signInForgotButton}</button>
        </form>
        <p className="mt-4 text-center"><button type="button" onClick={onBack} className={LINK}>{COPY.signInBackToSignIn}</button></p>
      </Shell>
    );
  }
  return (
    <Shell testId="portal-reset" title={COPY.signInResetTitle}>
      <p className="text-sm text-slate-600 mb-4" data-testid="portal-reset-sent">{COPY.signInResetSent}</p>
      <form onSubmit={confirm} className="space-y-4">
        <Field id="p-reset-code" label={COPY.signInResetCodeLabel} value={code} onChange={setCode} autoComplete="one-time-code" />
        <Field id="p-reset-pw" label={COPY.signInNewLabel} type="password" value={pw} onChange={setPw} autoComplete="new-password" />
        <Field id="p-reset-pw2" label={COPY.signInNewConfirmLabel} type="password" value={pw2} onChange={setPw2} autoComplete="new-password" />
        <PasswordRules pw={pw} />
        <Alert text={error} />
        <button type="submit" disabled={busy} className={BUTTON}>{busy ? COPY.signInResetBusy : COPY.signInResetButton}</button>
      </form>
      <p className="mt-4 text-center space-x-4">
        <button type="button" onClick={() => { setCode(''); sendCode(); }} className={LINK}>{COPY.signInResetResend}</button>
        <button type="button" onClick={onBack} className={LINK}>{COPY.signInBackToSignIn}</button>
      </p>
    </Shell>
  );
}

/** Amos-104 S1: change password while signed in (Cognito ChangePassword with the user's own access token). */
export function ChangePasswordScreen({ onDone, onCancel, onSessionExpired }: { onDone: () => void; onCancel: () => void; onSessionExpired: () => void }) {
  const [current, setCurrent] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (pw !== pw2) { setError(COPY.signInPasswordMismatch); return; }
    if (!passwordMeetsRules(pw)) { setError(COPY.signInPasswordWeak); return; }
    if (pw === current) { setError(COPY.accountSamePassword); return; }
    setBusy(true);
    try {
      await updatePassword({ oldPassword: current, newPassword: pw });
      setCurrent(''); setPw(''); setPw2('');
      onDone();
    } catch (err) {
      const n = errName(err);
      const msg = String((err as { message?: string })?.message || '');
      const policy = passwordPolicyMessage(err);
      if (policy) setError(policy);
      else if (n === 'UserUnAuthenticatedException' || (n === 'NotAuthorizedException' && /access token/i.test(msg))) onSessionExpired();
      else if (n === 'NotAuthorizedException' || n === 'InvalidParameterException') setError(COPY.accountWrongCurrent);
      else if (n === 'LimitExceededException' || n === 'TooManyRequestsException') setError(COPY.signInResetLimit);
      else { console.error('portal change-password error', err); setError(COPY.signInResetFailed); }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 w-full max-w-md mx-auto" data-testid="portal-change-password">
      <h2 className="text-lg font-semibold text-slate-800 mb-4">{COPY.accountChangeTitle}</h2>
      <form onSubmit={submit} className="space-y-4">
        <Field id="p-curpw" label={COPY.accountCurrentLabel} type="password" value={current} onChange={setCurrent} autoComplete="current-password" />
        <Field id="p-chgpw" label={COPY.signInNewLabel} type="password" value={pw} onChange={setPw} autoComplete="new-password" />
        <Field id="p-chgpw2" label={COPY.signInNewConfirmLabel} type="password" value={pw2} onChange={setPw2} autoComplete="new-password" />
        <PasswordRules pw={pw} />
        <Alert text={error} />
        <button type="submit" disabled={busy} className={BUTTON}>{busy ? COPY.accountChangeBusy : COPY.accountChangeButton}</button>
      </form>
      <p className="mt-4 text-center"><button type="button" onClick={onCancel} className={LINK}>{COPY.accountChangeCancel}</button></p>
    </div>
  );
}
